import { useState, useEffect, useMemo } from 'react'
import { Select } from 'antd'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import type { PracticeAgentReportData } from '../../types'
import { classifyColumns, pickLabelColumn, deriveChartDataForDimension, formatFieldDisplayName, getBarAxisMaxFallback, getBarAxisMin } from '../../utils'
import { PaginationBar } from './PaginationBar'
import { productThemes } from '../../../../lib/productBranding'

export function BarViz({ data, onSelect, onPageChange, onGroupByChange, sortColumn }: { data: PracticeAgentReportData; onSelect?: (field: string, value: string) => void; onPageChange?: (p: number) => void; onGroupByChange?: (dim: string) => void; sortColumn?: string | null }) {
  const { measureCols, dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const activeMeasure = useMemo(() => {
    if (sortColumn && measureCols.includes(sortColumn)) return sortColumn
    return measureCols[0] ?? null
  }, [sortColumn, measureCols])
  // Use backend-supplied display labels (FieldOptions.Name) as the group-by options so the API
  // receives a name it can resolve to the raw DAX ref via ReportBuilderOptions.
  const availableDimensions = data.dimensions?.length ? data.dimensions : dimensionCols
  const defaultDimension = useMemo(
    () => data.groupByDimension ?? pickLabelColumn(dimensionCols, data.rows),
    [data.groupByDimension, dimensionCols, data.rows],
  )
  const [selectedDimension, setSelectedDimension] = useState<string | null>(defaultDimension)
  useEffect(() => { setSelectedDimension(defaultDimension) }, [defaultDimension])

  const chartData = useMemo(() => {
    if (!selectedDimension) return data.chartData
    const derived = deriveChartDataForDimension(data.columns, data.rows, selectedDimension, activeMeasure)
    // Fall back to pre-built chartData when the display label doesn't match a raw column key
    return derived.length > 0 ? derived : data.chartData
  }, [data.columns, data.rows, data.chartData, selectedDimension, activeMeasure])

  if (chartData.length === 0) return <p className="text-sm text-gray-400 py-4">No chart data available.</p>
  const chartHeight = Math.max(160, chartData.length * 38 + 48)
  const values = chartData.map((point) => point.value)
  const xMin = getBarAxisMin(values)
  const xMax = data.axisMaxByMeasure?.[activeMeasure ?? ''] ?? data.axisMaxValue
  const xMaxFallback = getBarAxisMaxFallback(values)
  return (
    <div>
      {availableDimensions.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-500">Group by</span>
          <Select
            size="small"
            value={selectedDimension ?? undefined}
            onChange={(dim) => { setSelectedDimension(dim); onGroupByChange?.(dim) }}
            options={availableDimensions.map((col) => ({ value: col, label: formatFieldDisplayName(col) }))}
            style={{ minWidth: 140 }}
          />
        </div>
      )}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} domain={[xMin, xMax != null ? xMax : xMaxFallback]} />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={110} />
          <RechartsTooltip />
          <Bar
            dataKey="value"
            fill={productThemes.pracIntel.primary}
            radius={[0, 3, 3, 0]}
            isAnimationActive={false}
            name={formatFieldDisplayName(activeMeasure ?? chartData[0]?.series?.[0]?.name ?? 'Value')}
            cursor={onSelect ? 'pointer' : undefined}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick={(barData: any) => onSelect?.(selectedDimension ?? '', barData.label)}
          />
        </BarChart>
      </ResponsiveContainer>
      {onPageChange && data.totalRows != null && data.page != null && data.pageSize != null && (
        <PaginationBar
          page={data.page}
          totalRows={data.totalRows}
          pageSize={data.pageSize}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}
